import * as THREE from 'three';
import { Core } from './core/Core';
import { LightingService } from './core/LightingService';
import { Window } from './st/Window';
import { CameraService } from './core/CameraService';
import { Equipment } from './st/Equipment/Equipment';
import { ExerciseCameraAngles } from './data/CameraAngles';

import { BridgeActionResult, IncomingFlutterMessageType, OutgoingAsynchronousMessageType } from './data/BridgeData';
import { FlutterBridgeService } from './st/FlutterBridgeService';
import { EquipmentIds } from './data/EquipmentData';
import { Constants } from './st/Constants';
import { Mat } from './st/Mat';

import cardioBoxing from '../static/assets/animations/_dev_animations/cardioBoxing.json';
import test_leaning_balance from '../static/assets/animations/_dev_animations/test_leaning_balance.json';

declare global {
	interface Window { PlayerController: any; }
}

export class ExerciseScene {
	/**
	 * System Properties
	 * */
	core: Core;
	window?: Window;
	mat?: Mat;
	equipment: Equipment[] = [];

	/**
	 * Responsive
	 */
	//boundingBoxZoomInterval: any;
	//boundingBoxCheckRateMS: number = 2500;

	/**
	 * Properties
	 * */
	constructor(core: Core) {
		this.core = core;

		// Create Lights
		const sLighting: LightingService = this.core.s('Lighting');
		const ambientLight = sLighting.createAmbientLight("WorldAmbient", "#ffffff", 3);
		sLighting.enableLight(ambientLight);
		const directionalLight1 = sLighting.createDirectionalLight("Directional", new THREE.Vector3(40, 50, 50), "#ffffff", 3);
		sLighting.enableLight(directionalLight1);
		sLighting.addShadowsToLight(directionalLight1);

		// Create cameras
		const sCamera: CameraService = this.core.s('Camera');
		sCamera.registerCameraAngles(ExerciseCameraAngles)
		sCamera.switchCameraAngle(ExerciseCameraAngles[0]);

		// Start rendering
		const sTick = this.core.s('Tick');
		sTick.start();

		// Register a character loaded callback
		this.core.s('Character').registerOnCharacterLoadedCallback(this.onCharacterLoaded.bind(this));
	}

	/**
	 * Callback when the character has loaded
	 */
	onCharacterLoaded() {
		// Set an interval to check the camera and zoom in and out to match the character group
		//const sCamera = this.core.s('Camera');
		//this.boundingBoxZoomInterval = setInterval(sCamera.adjustCameraPositionToViewGroup.bind(sCamera, this.character.characterGroup), this.boundingBoxCheckRateMS);

		this.createEquipment();

		// Notify the app that we are ready for additional commands
		const sFlutterBridge: FlutterBridgeService = this.core.s('FlutterBridge');
		sFlutterBridge.sendAsynchronousMessage({ type: OutgoingAsynchronousMessageType.player_ready, payload: { result: 'ok' } });

		
		this.setupDemoMode();
	}

	/**
	 * Sets up a demonstration mode
	 */
	setupDemoMode() {
		const sFlutterBridge: FlutterBridgeService = this.core.s('FlutterBridge');
		sFlutterBridge.receiveFlutterMessage({
			event_type: IncomingFlutterMessageType.register_animation, payload: {
				exercise_variant_id: "Cardio Bloxing",
				animation_json: cardioBoxing,
				playback_speed: 1
			}
		});

		sFlutterBridge.receiveFlutterMessage({
			event_type: IncomingFlutterMessageType.register_animation, payload: {
				exercise_variant_id: "Leaning Balance",
				animation_json: test_leaning_balance,
				playback_speed: 1
			}
		});
	}

	/**
	 * Plays the demo mode: leaning
	 */
	cycleDemoModeLeaning() {
		const sFlutterBridge: FlutterBridgeService = this.core.s('FlutterBridge');
		sFlutterBridge.receiveFlutterMessage({ event_type: IncomingFlutterMessageType.soft_idle, payload: null });
		sFlutterBridge.receiveFlutterMessage({
			event_type: IncomingFlutterMessageType.set_camera, payload: {
				camera_angle: Math.random() > 0.5 && 'topLeft' || 'topRight'
			}
		});
		sFlutterBridge.receiveFlutterMessage({
			event_type: IncomingFlutterMessageType.play_animation, payload: {
				exercise_variant_id: "Leaning Balance",
			}
		});

		setTimeout(this.cycleDemoModeBoxing.bind(this), 7500);
	}

	/**
	 * Plays the demo mode: boxing
	 */
	cycleDemoModeBoxing() {
		const sFlutterBridge: FlutterBridgeService = this.core.s('FlutterBridge');
		sFlutterBridge.receiveFlutterMessage({ event_type: IncomingFlutterMessageType.soft_idle, payload: null });
		sFlutterBridge.receiveFlutterMessage({
			event_type: IncomingFlutterMessageType.set_camera, payload: {
				camera_angle: Math.random() > 0.5 && 'frontLeft' || 'frontRight'
			}
		});
		sFlutterBridge.receiveFlutterMessage({
			event_type: IncomingFlutterMessageType.play_animation, payload: {
				exercise_variant_id: "Cardio Bloxing",
			}
		});

		setTimeout(this.cycleDemoModeLeaning.bind(this), 7500);
	}

	/**
	 * Creates equipment
	 */
	createEquipment() {
		this.mat = new Mat(this.core, true);
		
		setTimeout(() => this.equipment.find(equipment => equipment.ids.includes(EquipmentIds.mat))?.fadeEquipment(), 800);
	}

	/**
	 * Shows any hidden equipment, and hides any not used
	 */
	setVisibleEquipment(equipmentIds: string[]): BridgeActionResult {
		const equipmentOn = this.equipment.filter(equipment => equipment.ids.some(id => equipmentIds.includes(id)));
		const linkedEquipment = new Set<Equipment>();
		equipmentOn.forEach(equipment => equipment.linkedEquipment.forEach(linkedItem => linkedEquipment.add(this.equipment.find(equipment => equipment.ids.includes(linkedItem))!)));
		equipmentOn.push(...Array.from(linkedEquipment));
		const equipmentOff = this.equipment.filter(equipment => !equipmentOn.includes(equipment));

		// Hide not required items, and set a timeout to show the others, to avoid a rendering quirk
		equipmentOff.forEach(equipment => equipment.isShowing && equipment.fadeEquipment(false));
		setTimeout(() => {
			equipmentOn.forEach(equipment => !equipment.isShowing && equipment.fadeEquipment(true));
		}, Constants.equipmentFadeDurationMS);

		return new BridgeActionResult(true);
	}

	/**
	 * Hides all equipment
	 */
	hideAllEquipment(): BridgeActionResult {
		this.equipment.forEach((equipmentItem: Equipment) => {
			if (equipmentItem.isShowing) equipmentItem.fadeEquipment(false);
		});

		return new BridgeActionResult(true);
	}
}